import axios from "axios";
import { signOutSuccess } from "store/slices/authSlice";
import store from "../store";
import { AUTH_TOKEN, REFRESH_TOKEN, USER_ROLES } from "constants/AuthConstant";
import { SSO_REFRESH_TOKEN } from "constants/ApiConstant";
import { notification } from "antd";
import { setAuthenData } from "utils/helper";

const TOKEN_PAYLOAD_KEY = "authorization";
const MAX_QUEUE_SIZE = 10;

let isRefreshing = false;
let isSessionExpiredNotified = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    error ? prom.reject(error) : prom.resolve(token);
  });
  failedQueue = [];
};

const clearAuth = () => {
  localStorage.removeItem(AUTH_TOKEN);
  localStorage.removeItem(REFRESH_TOKEN);
  localStorage.removeItem(USER_ROLES);
  store.dispatch(signOutSuccess());
};

const handleSessionExpired = () => {
  if (!isSessionExpiredNotified) {
    isSessionExpiredNotified = true;

    clearAuth();

    notification.error({
      message: "Phiên đăng nhập hết hạn",
      description: "Xin vui lòng đăng nhập lại",
      key: "session-expired"
    });

    setTimeout(() => {
      isSessionExpiredNotified = false;
    }, 5000);
  }
};

const service = axios.create({
  baseURL: process.env.REACT_APP_API_ENDPOINT_URL,
  timeout: 60000,
});

// ─── Request interceptor ──────────────────────────────────────────────────────
service.interceptors.request.use(
  (config) => {
    const jwtToken = localStorage.getItem(AUTH_TOKEN);
    if (jwtToken) {
      config.headers[TOKEN_PAYLOAD_KEY] = `Bearer ${jwtToken}`;
    }
    return config;
  },
  (error) => {
    notification.error({ message: "Lỗi cấu hình request" });
    return Promise.reject(error);
  }
);

// ─── Response interceptor ─────────────────────────────────────────────────────
service.interceptors.response.use(
  (response) => response.data,

  async (error) => {
    const originalRequest = error.config;

    if (error._handled) return Promise.reject(error);

    if (!error.response) {
      notification.error({ message: "Lỗi kết nối mạng" });
      return Promise.reject(error);
    }

    const { status, data } = error.response;

    // ── 601: Token hết hạn → thử refresh ─────────────────────────────────────
    if (status === 601 && !originalRequest._retry) {

      // Đang refresh rồi → đẩy vào queue, chờ token mới
      if (isRefreshing) {
        if (failedQueue.length >= MAX_QUEUE_SIZE) {
          return Promise.reject(new Error("Queue overflow"));
        }
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token) => {
              originalRequest._retry = true;
              originalRequest.headers[TOKEN_PAYLOAD_KEY] = `Bearer ${token}`;
              resolve(service(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem(REFRESH_TOKEN);
        if (!refreshToken) throw new Error("No refresh token");

        const response = await axios.post(
          SSO_REFRESH_TOKEN,
          { refreshToken },
          {
            baseURL: process.env.REACT_APP_IDENTITY_ENDPOINT_URL,
            timeout: 10000,
          }
        );

        if (!response.data?.data) throw new Error("Invalid refresh response");

        const newTokenData = response.data.data;
        setAuthenData(newTokenData);

        const newToken = newTokenData.tokenData.token;
        service.defaults.headers.common[TOKEN_PAYLOAD_KEY] = `Bearer ${newToken}`;
        originalRequest.headers[TOKEN_PAYLOAD_KEY] = `Bearer ${newToken}`;

        isRefreshing = false;
        processQueue(null, newToken);

        return service(originalRequest);
      } catch (_error) {
        isRefreshing = false;
        processQueue(_error, null);
        handleSessionExpired();
        return Promise.reject({ ..._error, _handled: true });
      }
    }

    // ── Các lỗi còn lại ───────────────────────────────────────────────────────
    const notificationMap = {
      [401]: { message: "Lỗi xác thực", description: "Bạn không có quyền thao tác" },
      [403]: { message: "Lỗi xác thực", description: "Bạn không có quyền thao tác" },
      [602]: { message: "Tài khoản đã bị khóa" },
      [404]: { message: "Không tìm thấy tài nguyên" },
      [500]: { message: "Lỗi máy chủ", description: "Vui lòng thử lại sau" },
      [508]: { message: "Yêu cầu quá thời gian chờ" },
    };

    if (notificationMap[status]) {
      notification.error(notificationMap[status]);
    } else if (status === 600) {
      notification.error({
        message: `Lỗi (${data?.errorCode}): ${data?.description}`,
      });
    } else if (status === 400) {
      notification.error({
        message: `Lỗi (${data?.status}): ${data?.title}`,
      });
    }

    return Promise.reject(error);
  }
);

export default service;